import { Suspense } from 'react';
import { useQueryState } from 'nuqs';
import {
  Grid,
  Page,
  PageBody,
  Panel,
  PanelHeader,
  PanelSection,
  PanelTitle,
  Tabs,
  TabsList,
  TabsPanel,
  TabsTab,
} from '@customerio/pluma-components/react';
import { usePermissions } from '@customerio/hydra-permissions/react';
import { useAnalytics } from '@/services/analytics';
import { usePageViewTracking } from '@/hooks/use-page-view-tracking';
import { DocumentTitle } from '@/components/page/document-title';
import { LoadingPage } from '@/components/page/loading';
import { useEnvironmentOptional } from '@/services/db/environment/queries';
import { useObjectTypes } from '@/services/db/object-type/queries';
import { requireWorkspaceId } from '@/services/db/shared/workspace';
import type { DataIndexItemAddress } from '../urls';
import { DetailsPanel } from './details-panel';
import { EventAttributesTable } from './event-attributes-table';
import { ItemHeader } from './item-header';
import { useDataIndexItem, useItemReach, useItemTags } from './hooks';
import { reachHref, useItemPageModel } from './page-model';
import { RecentActivity } from './recent-activity';
import { itemTabs, resolveItemTab } from './tabs';
import { UsageTab } from './usage-tab';

/**
 * One attribute's or event's own page.
 *
 * The detail read is the page: everything on it — what uses the item, what
 * can be done to it, and whether it can be deleted at all — comes out of the
 * same record, so the page suspends on it rather than assembling itself in
 * pieces.
 */

type DataIndexItemPageProps = {
  address: DataIndexItemAddress;
};

export default function DataIndexItemPage({ address }: DataIndexItemPageProps) {
  return (
    <Page>
      <DocumentTitle title={address.name} />
      <Suspense fallback={<LoadingPage />}>
        <DataIndexItemView address={address} />
      </Suspense>
    </Page>
  );
}

type DataIndexItemViewProps = {
  address: DataIndexItemAddress;
};

function DataIndexItemView({ address }: DataIndexItemViewProps) {
  const workspaceId = requireWorkspaceId();
  const item = useDataIndexItem(workspaceId, address);
  const tags = useItemTags(workspaceId, item.tagIds);
  const environment = useEnvironmentOptional();
  const objectTypes = useObjectTypes().data;
  const { hasPermission } = usePermissions();
  useItemViewTracking(address);

  const identifiers = environment.data?.environment?.identifiers;
  const model = useItemPageModel({
    address,
    item,
    objectTypes,
    identifiers,
    isEnvironmentLoading: environment.data == null,
    canDelete: hasPermission('journeys/people/edit'),
  });
  const reach = useItemReach(workspaceId, address, model.reach.withRow);

  return (
    <>
      <ItemHeader
        workspaceId={workspaceId}
        address={address}
        item={item}
        description={model.description}
        tags={tags}
        identifiers={identifiers}
        canBeMarkedSensitive={model.canBeMarkedSensitive}
        deleteBlockReason={model.deleteBlockReason}
      />

      <PageBody>
        <Grid gap="300" w="full" columns="1fr 2fr">
          <Panel>
            <PanelHeader>
              <PanelTitle>Details</PanelTitle>
            </PanelHeader>
            <PanelSection>
              <DetailsPanel
                item={item}
                kind={address.kind}
                tags={tags}
                usage={reach}
                reachHref={reachHref(workspaceId, address, reach.filters)}
                terms={model.reachTerms}
                reach={model.reach}
                // Whether an attribute is redacted is only the viewer's
                // business when they could change it.
                withSensitivity={address.kind === 'attribute' && hasPermission('journeys/attribute_metadata/edit')}
                campaignIndefinite={model.campaignIndefinite}
              />
            </PanelSection>
          </Panel>

          <ItemPanels address={address} item={item} model={model} workspaceId={workspaceId} />
        </Grid>
      </PageBody>
    </>
  );
}

/**
 * The item's own view event, fired once per item rather than per navigation:
 * changing tab writes the URL and is the same view of the same item.
 */
function useItemViewTracking(address: DataIndexItemAddress) {
  const analytics = useAnalytics();
  const event = address.kind === 'attribute' ? 'dataIndexAttributeViewed' : 'dataIndexEventViewed';

  usePageViewTracking(
    () => {
      void analytics.track('logs', event, {});
    },
    true,
    { ignoreSearch: true },
  );
}

type ItemPanelsProps = {
  workspaceId: number;
  address: DataIndexItemAddress;
  item: ReturnType<typeof useDataIndexItem>;
  model: ReturnType<typeof useItemPageModel>;
};

function ItemPanels({ workspaceId, address, item, model }: ItemPanelsProps) {
  const [tab, setTab] = useQueryState('tab');
  const { hasPermission } = usePermissions();
  const canEditMetadata = hasPermission('journeys/attribute_metadata/edit');

  // Only an event has attributes of its own; whether it has any yet does not
  // decide the tab. The page this replaces gates it on the
  // `data_index_event_attribute` flag, which is on for every account, and
  // shows a zero badge for an event nothing has been sent for.
  const withEventAttributes = address.kind === 'event';
  const activeTab = resolveItemTab(tab, withEventAttributes);

  return (
    <Tabs gap="200" activeTab={activeTab} onChange={(next) => void setTab(next)}>
      <TabsList>
        {itemTabs({ usage: model.usageCount, eventAttributes: item.eventAttributes.length }, withEventAttributes).map(
          (option) => (
            <TabsTab key={option.value} value={option.value} badge={option.badge}>
              {option.label}
            </TabsTab>
          ),
        )}
      </TabsList>
      <TabsPanel value="usage">
        <UsageTab
          workspaceId={workspaceId}
          itemName={item.name}
          usage={item.usage}
          scope={model.usageScope}
          terms={model.usageTerms}
        />
      </TabsPanel>
      <TabsPanel value="activity">
        <RecentActivity workspaceId={workspaceId} address={address} />
      </TabsPanel>
      {withEventAttributes ? (
        <TabsPanel value="attributes">
          <EventAttributesTable
            workspaceId={workspaceId}
            eventName={item.name}
            attributes={item.eventAttributes}
            canMarkSensitive={model.canRedact && canEditMetadata}
          />
        </TabsPanel>
      ) : null}
    </Tabs>
  );
}
